import React, { useEffect, useRef, useState } from "react";
import { api } from "../../../api";
import useEscapeLayer from "../../useEscape";

/*
 * THE CLIENT BRIEF, REVIEWED BEFORE IT GOES TO SLACK.
 *
 * Gabriel, 2026-10-15, on the first deployed card: "I'd want to be able to review
 * it before sending." So Post to Slack lives here, at the foot of the brief, and
 * nowhere else: the card's yellow button opens this, and nothing reaches Slack
 * until the button down here is pressed.
 *
 * WHAT IS SHOWN IS WHAT IS POSTED. The server sends the same blocks the canvas is
 * rendered from (src/brief/canvas.js briefBlocks), and this draws them the way
 * the canvas does: the header callout, the glance table, the watch-outs, the two
 * kickoff checklists, the detail sections with their quotes, what it was read
 * from.
 *
 * WHERE IT GOES IS SAID BEFORE THE CLICK. The foot names the channel the server
 * found ("Posts to #liquid_instruments"), and the button carries it. When none
 * was found, or Slack is not connected, it says why and asks for the channel,
 * with this client's own name as the example, never another client's.
 *
 * Once posted, the foot is a link to the canvas instead; a later Rewrite on the
 * card replaces that canvas in place.
 *
 * Props:
 *   companyId  the company
 *   onClose    () => void
 *   onPosted   (status) => void, with the card's new status after a post
 */
export default function ClientBriefReview({ companyId, onClose, onPosted }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [channel, setChannel] = useState("");
  const [other, setOther] = useState(false); // typing a channel other than the one found
  const [busy, setBusy] = useState(false);
  const [postError, setPostError] = useState(null);
  const ref = useRef(null);
  useEscapeLayer(true, onClose);

  useEffect(() => {
    let live = true;
    api(`/companies/${companyId}/brief/review`)
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e.message));
    return () => {
      live = false;
    };
  }, [companyId]);

  useEffect(() => {
    if (ref.current) ref.current.focus({ preventScroll: true });
  }, []);

  const target = (data && data.target) || null;
  const posted = !!(data && data.slack && data.slack.canvas_url);
  const typed = channel.trim().replace(/^#/, "");
  const goesTo = other ? typed || null : typed || (target && target.channel) || null;
  const example = target && target.looked_for && target.looked_for[0] ? `#${target.looked_for[0]}` : "#client-channel";

  async function post() {
    setBusy(true);
    setPostError(null);
    try {
      const next = await api(`/companies/${companyId}/brief/slack`, { method: "POST", body: JSON.stringify(typed ? { channel: typed } : {}) });
      if (next.slack && next.slack.canvas_url) {
        setData((d) => ({ ...d, slack: next.slack }));
        onPosted && onPosted(next);
      } else {
        setPostError((next.slack && next.slack.error) || "It didn’t reach Slack.");
      }
    } catch (e) {
      setPostError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rec-dialog__backdrop rec-brief__backdrop" onClick={onClose}>
      <div className="rec-confirm rec-brief" role="dialog" aria-modal="true" aria-label="Review the Initial Client Brief" tabIndex={-1} ref={ref} onClick={(e) => e.stopPropagation()}>
        <div className="rec-brief__head">
          <div>
            <div className="rec-confirm__title">Initial Client Brief</div>
            <div className="rec-meta">The canvas as it will read in Slack. Nothing is posted until you press the button below.</div>
          </div>
          <button type="button" className="rec-close" title="Close (Esc)" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="rec-brief__body">
          {error && <div className="rec-err">Couldn’t load the brief: {error}</div>}
          {!data && !error && <div className="rec-meta">Loading…</div>}
          {data && (data.blocks || []).map((b, i) => <Block key={i} block={b} />)}
        </div>

        <div className="rec-brief__foot">
          {posted ? (
            <>
              <span className="rec-sub">
                In #{data.slack.channel}. A Rewrite on the card replaces this canvas.
              </span>
              <a className="rec-btn rec-btn--secondary" href={data.slack.canvas_url} target="_blank" rel="noopener noreferrer">
                Open in Slack
              </a>
            </>
          ) : (
            data &&
            !error && (
              <>
                <div className="rec-brief__target">
                  {target && target.channel && !other ? (
                    <span className="rec-sub">
                      Posts to <b>#{target.channel}</b> as a canvas, with a short message in the channel.{" "}
                      <button type="button" className="rec-link" onClick={() => setOther(true)}>
                        Another channel
                      </button>
                    </span>
                  ) : (
                    <span className="rec-sub">{target && target.error ? `${target.error}. ` : ""}Post to:</span>
                  )}
                  {(!target || !target.channel || other) && (
                    <input
                      className="rec-input rec-brief__channel"
                      placeholder={example}
                      value={channel}
                      disabled={busy}
                      onChange={(e) => setChannel(e.target.value)}
                      aria-label="Channel to post to"
                    />
                  )}
                  {postError && <span className="rec-err">{postError}</span>}
                </div>
                <button type="button" className="rec-btn rec-btn--primary rec-brief__post" disabled={busy || !goesTo} onClick={post}>
                  {busy ? "Posting…" : goesTo ? `Post to #${goesTo}` : "Post to Slack"}
                </button>
              </>
            )
          )}
        </div>
      </div>
    </div>
  );
}

function Cite({ text }) {
  return text ? <span className="rec-brief__cite">{text}</span> : null;
}

function Item({ item }) {
  return (
    <>
      <span>
        {item.lead && <b>{item.lead}.</b>} {item.text} <Cite text={item.cite} />
      </span>
      {item.quote && <blockquote className="rec-brief__quote">“{item.quote}”</blockquote>}
    </>
  );
}

function Block({ block: b }) {
  if (b.type === "header") {
    return (
      <div className="rec-brief__callout rec-brief__callout--head">
        <div className="rec-brief__name">
          {b.icon} {b.title}
        </div>
        <p>{b.text}</p>
        <div className="rec-brief__cite">{b.meta}</div>
      </div>
    );
  }
  const heading = (
    <h3 className="rec-brief__h">
      <span aria-hidden="true">{b.icon}</span> {b.title}
    </h3>
  );
  if (b.type === "glance") {
    return (
      <section>
        {heading}
        <div className="rec-brief__glance">
          {b.rows.map((r) => (
            <React.Fragment key={r.label}>
              <span className="rec-brief__label">{r.label}</span>
              <span>
                {r.value === null ? <span className="rec-brief__none">Not discussed</span> : r.value} <Cite text={r.cite} />
              </span>
            </React.Fragment>
          ))}
        </div>
      </section>
    );
  }
  if (b.type === "callouts") {
    return (
      <section>
        {heading}
        {b.items.map((item, i) => (
          <div key={i} className="rec-brief__callout rec-brief__callout--warn">
            <span aria-hidden="true">⚠️</span> <Item item={item} />
          </div>
        ))}
      </section>
    );
  }
  if (b.type === "checklists") {
    return (
      <section>
        {heading}
        {b.lists.map((l) => (
          <div key={l.title} className="rec-brief__list">
            <h4 className="rec-brief__h4">{l.title}</h4>
            {l.items.length ? (
              <ul className="rec-brief__items rec-brief__items--check">
                {l.items.map((item, i) => (
                  <li key={i}>
                    <span className="rec-brief__box" aria-hidden="true" />
                    <div>
                      <Item item={item} />
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="rec-brief__none">{l.empty}</div>
            )}
          </div>
        ))}
      </section>
    );
  }
  if (b.type === "section") {
    return (
      <section>
        {heading}
        {b.items.length ? (
          <ul className="rec-brief__items">
            {b.items.map((item, i) => (
              <li key={i}>
                <Item item={item} />
              </li>
            ))}
          </ul>
        ) : (
          <div className="rec-brief__none">{b.empty}</div>
        )}
      </section>
    );
  }
  if (b.type === "sources") {
    return (
      <section className="rec-brief__sources">
        {heading}
        <ul>
          {b.lines.map((l, i) => (
            <li key={i}>{l}</li>
          ))}
        </ul>
      </section>
    );
  }
  return null;
}
